import { BarChart3, Calendar, PieChart, Target, TrendingDown, TrendingUp, Users } from "lucide-react";
import React, { useMemo } from "react";
import type { ProductTagsData } from "../../../types/productTags";
import { formatUtcDate } from "../../../utils/dateUtils";
import { formatSignedPercentage } from "../../../utils/numberUtils";
import ProductTagsBadges from "../../ProductTagsBadges";
import { DisplayPortfolioData } from "./types";

interface PortfolioHeroSectionProps {
  portfolio: DisplayPortfolioData;
  oneYearPerformance: number | null;
  latestMetricDate?: string | null;
  isDarkMode: boolean;
  showProviderInfo?: boolean;
  /** Product tags to display */
  tags?: ProductTagsData | null;
  /** Callback when tags are clicked */
  onTagsClick?: () => void;
}

const formatDate = (dateString: string): string => {
  return formatUtcDate(dateString, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const PortfolioHeroSection: React.FC<PortfolioHeroSectionProps> = ({
  portfolio,
  oneYearPerformance,
  latestMetricDate,
  isDarkMode,
  showProviderInfo = true,
  tags,
  onTagsClick,
}) => {
  const providerName = portfolio.provider?.name || portfolio.providers?.name || "Investment Provider";

  // Count positions
  const { totalStocks, totalOptions, totalPositions, basketCount } = useMemo(() => {
    const basketStockCount =
      portfolio.portfolioBaskets?.reduce((total, basket) => {
        const b = basket.basket || basket.baskets;
        return total + (b?.basketStocks?.length || 0);
      }, 0) || 0;
    const computedStocks = (portfolio.portfolioStocks?.length || 0) + basketStockCount;
    const computedOptions = portfolio.portfolioOptions?.length || 0;
    const computedBaskets = portfolio.portfolioBaskets?.length || 0;
    const providedStocks = Number.isFinite(portfolio.numberOfStocks) ? portfolio.numberOfStocks : null;
    const providedOptions = Number.isFinite(portfolio.numberOfOptions) ? portfolio.numberOfOptions : null;
    const providedBaskets = Number.isFinite(portfolio.numberOfBaskets) ? portfolio.numberOfBaskets : null;
    const totalStocks = providedStocks ?? computedStocks;
    const totalOptions = providedOptions ?? computedOptions;
    const basketCount = providedBaskets ?? computedBaskets;
    return {
      totalStocks,
      totalOptions,
      totalPositions: totalStocks + totalOptions,
      basketCount,
    };
  }, [portfolio]);

  return (
    <div
      className={`${isDarkMode ? "bg-gray-800" : "bg-white/80 backdrop-blur-sm"} rounded-2xl p-6 sm:p-8 shadow-xl border ${
        isDarkMode ? "border-gray-700" : "border-gray-200/50"
      }`}
    >
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-6">
        {/* Left side - Portfolio Info */}
        <div className="flex-1 text-center lg:text-left">
          <h1
            className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${
              isDarkMode ? "text-white" : "text-gray-900"
            } mb-3`}
          >
            {portfolio.name}
          </h1>
          {portfolio.description && (
            <p
              className={`${isDarkMode ? "text-gray-300" : "text-gray-700"} text-sm sm:text-base leading-relaxed mb-4`}
            >
              {portfolio.description}
            </p>
          )}
          {showProviderInfo && (
            <div
              className={`flex items-center justify-center lg:justify-start space-x-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              } mb-2`}
            >
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">by {providerName}</span>
            </div>
          )}
          {/* Product Tags */}
          <ProductTagsBadges
            tags={tags}
            onClick={onTagsClick}
            className="justify-center lg:justify-start"
          />
        </div>

        {/* Right side - Performance Number */}
        {oneYearPerformance !== null && (
          <div className="flex flex-col items-center lg:items-end">
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full mb-2 ${
                isDarkMode ? "bg-gray-700" : oneYearPerformance >= 0 ? "bg-teal-50" : "bg-red-50"
              }`}
            >
              {oneYearPerformance >= 0 ? (
                <TrendingUp className={`w-4 h-4 mr-1 ${isDarkMode ? "text-teal-400" : "text-teal-600"}`} />
              ) : (
                <TrendingDown className={`w-4 h-4 mr-1 ${isDarkMode ? "text-red-400" : "text-red-600"}`} />
              )}
              <span
                className={`text-xs font-semibold ${
                  isDarkMode ? "text-gray-300" : oneYearPerformance >= 0 ? "text-teal-700" : "text-red-700"
                }`}
              >
                1Y Performance
              </span>
            </div>
            <p
              className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-none mb-1 ${
                isDarkMode
                  ? oneYearPerformance >= 0
                    ? "text-teal-400"
                    : "text-red-400"
                  : oneYearPerformance >= 0
                  ? "text-teal-600"
                  : "text-red-600"
              }`}
            >
              {formatSignedPercentage(oneYearPerformance, 1)}
            </p>
            {latestMetricDate && (
              <p className={`text-[10px] sm:text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mt-0.5`}>
                as of {formatDate(latestMetricDate)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
        <div
          className={`text-center p-4 ${
            isDarkMode ? "bg-gray-700" : "bg-gradient-to-br from-blue-50 to-blue-100"
          } rounded-xl border ${isDarkMode ? "border-gray-600" : "border-blue-200"}`}
        >
          <div
            className={`flex items-center justify-center w-10 h-10 ${
              isDarkMode ? "bg-blue-900" : "bg-blue-500"
            } rounded-lg mx-auto mb-2`}
          >
            <Target className="w-5 h-5 text-white" />
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{totalPositions}</p>
          <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"} mt-1`}>Total Positions</p>
        </div>

        <div
          className={`text-center p-4 ${
            isDarkMode ? "bg-gray-700" : "bg-gradient-to-br from-green-50 to-green-100"
          } rounded-xl border ${isDarkMode ? "border-gray-600" : "border-green-200"}`}
        >
          <div
            className={`flex items-center justify-center w-10 h-10 ${
              isDarkMode ? "bg-green-900" : "bg-green-500"
            } rounded-lg mx-auto mb-2`}
          >
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{totalStocks}</p>
          <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"} mt-1`}>Stocks</p>
        </div>

        <div
          className={`text-center p-4 ${
            isDarkMode ? "bg-gray-700" : "bg-gradient-to-br from-purple-50 to-purple-100"
          } rounded-xl border ${isDarkMode ? "border-gray-600" : "border-purple-200"}`}
        >
          <div
            className={`flex items-center justify-center w-10 h-10 ${
              isDarkMode ? "bg-purple-900" : "bg-purple-500"
            } rounded-lg mx-auto mb-2`}
          >
            <PieChart className="w-5 h-5 text-white" />
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{basketCount}</p>
          <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"} mt-1`}>Baskets</p>
        </div>

        <div
          className={`text-center p-4 ${
            isDarkMode ? "bg-gray-700" : "bg-gradient-to-br from-orange-50 to-orange-100"
          } rounded-xl border ${isDarkMode ? "border-gray-600" : "border-orange-200"}`}
        >
          <div
            className={`flex items-center justify-center w-10 h-10 ${
              isDarkMode ? "bg-orange-900" : "bg-orange-500"
            } rounded-lg mx-auto mb-2`}
          >
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <p className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{totalOptions}</p>
          <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"} mt-1`}>Options</p>
        </div>
      </div>
    </div>
  );
};

export default PortfolioHeroSection;
